import { Box, Grid, TextField as MUITextField, Skeleton } from "@mui/material";
import { useEffect, useRef, useState } from "react";


import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import { useSnackbar } from "notistack";
import { User } from "./types";
import { axios } from "../../api";
import queryString from "query-string";

export interface UserAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  label?: string;
  companyId?: string;
}

export function UserAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(props: UserAutocompleteProps<User, Multiple, DisableClearable, FreeSolo>) {
  const { label, companyId, ...autocompleteProps } = props;
  const sx = (props as any).sx;
  const [users, setUsers] = useState<User[]>();
  let isRendered = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { name } = fieldToTextField(props as any);

  useEffect(() => {
    isRendered.current = true;
    const params = {
      filter: [JSON.stringify({ field: "active", op: "==", value: "1" })],
      size: 100,
      page: 1,
      sort: "name",
    };

    if (companyId) {
      params["company_id"] = companyId
      // params["filter"].push(
      //   JSON.stringify({ field: "company_id", op: "==", value: companyId })
      // );
    }

    axios
      .get("/v6/users", {
        params: params,
        paramsSerializer: (params) => queryString.stringify(params),
      })
      .then((resp) => {
        if (isRendered.current) {
          let usersList = resp.data.items;
          
          // If we have a current value that's not in the results, add it to ensure it's available
          const currentValue = autocompleteProps.field.value;
          if (currentValue && typeof currentValue === 'object' && currentValue.id) {
            const existsInResults = usersList.find(user => user.id === currentValue.id);
            if (!existsInResults) {
              usersList.push(currentValue);
            }
          }
          
          setUsers(usersList);
        }
      })
      .catch(() => {
        enqueueSnackbar("Error fetching users", {
          variant: "error",
        });
      });

    return () => {
      isRendered.current = false;
    };
  }, [enqueueSnackbar, companyId, autocompleteProps.field.value]);

  return (
    <Box sx={sx}>
      {users ? (
        <MuiAutocomplete
          {...fieldToAutocomplete(autocompleteProps)}
          fullWidth
          autoComplete
          includeInputInList
          limitTags={3}
          options={users}
          disableCloseOnSelect={!!props.multiple}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            } else {
              if (option.name) {
                return option.name;
              } else {
                const user = users.find((d) => option.id === d.id);
                return user?.name || "";
              }
            }
          }}
          isOptionEqualToValue={(option: User, value: User) => {
            // Handle null/undefined values
            if (!option || !value) {
              return option === value;
            }
            
            // Handle string values (empty string case)
            if (typeof value === "string") {
              return value === "";
            }
            
            // Compare by ID for user objects
            if (option.id === value.id) {
              // Ensure value has name property for display
              if (!Object.prototype.hasOwnProperty.call(value, "name")) {
                value.name = option.name;
              }
              return true;
            }
            
            return false;
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <MUITextField
              {...params}
              label={label}
              fullWidth
              inputProps={{
                ...params.inputProps,
              }}
              error={props.form.touched[name!] && !!props.form.errors[name!]}
              // @ts-ignore
              helperText={props.form.touched[name!] && props.form.errors[name!]}
              variant="outlined"
            />
          )}
          renderOption={(props, option: User, state) => {
            const matches = match(option.name, state.inputValue);
            const users = parse(option.name, matches);

            return (
              <li {...props} key={option.id}>
                <Grid container alignItems="center">
                  
                  <Grid item xs>
                    {users.map((user, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: user.highlight ? 700 : 400,
                        }}
                      >
                        {user.text}
                      </span>
                    ))}
                  </Grid>
                </Grid>
              </li>
            );
          }}
        />
      ) : (
        <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
      )}
    </Box>
  );
}

UserAutocomplete.displayName = "FormikMaterialUIUserAutocomplete";
